<script>
import Button from "@/components/atoms/button";
import Divider from "@/components/atoms/divider";
import Container from "@/components/atoms/container";
import List from "@/components/molecules/list";
import ListItem from "@/components/molecules/list/list-item";
import Icon from "@/components/atoms/icon";
import Titre from "@/components/atoms/titre";
import Form from "@/components/molecules/form";
import FormCheckbox from "@/components/molecules/form-checkbox";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import FormRadio from "@/components/molecules/form-radio";
import FormError from "@/components/molecules/form/form-error";
import StateOverlay from "@/components/molecules/state-overlay";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

export default {
  name: "SuppressionDeCompte",
  components: {
    Button,
    FormInput,
    FormGroup,
    Divider,
    List,
    ListItem,
    FormCheckbox,
    Icon,
    Titre,
    FormRadio,
    Container,
    FormError,
    StateOverlay,
    Form
  },
  props: {
    etape: {
      type: String,
      required: true,
      default: "step1"
    },
    motifs: {
      type: Array,
      required: false,
      default: () => []
    },
    errorMotifs: {
      type: Error,
      required: false,
      default: null
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false
    },
    hasAliasPaiement: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  emits: ["supprimerCompte", "close", "step2"],
  setup(props) {
    const { v$, checkValidation } = useFormValidation(props);
    return {
      v$,
      checkValidation
    };
  },
  data() {
    return {
      abonnements: [],
      commandes: [],
      isChecked: false,
      model: {
        password: "",
        motif_id: null
      }
    };
  },
  methods: {
    supprimerCompte() {
      if (this.checkValidation(this.v$)) {
        this.$emit("supprimerCompte", this.model);
      }
    }
  }
};
</script>

<template>
  <Container size="xs">
    <div class="u-flex u-flex-column u-flex-align-center u-pb-sm u-pb-md@sm">
      <template v-if="etape === 'step2'">
        <Icon
          name="alert-circle"
          variant="primary"
          size="lg"
          class="u-mb-sm"
        />
        <Titre
          tag="h1"
          variant="primary"
          size="md"
          class="u-text-center u-mb-lg"
        >
          Dernière étape<br>avant la suppression de votre compte Internet
        </Titre>
        <Titre
          tag="h2"
          size="sm"
          class="u-text-center u-mb-md"
        >
          Pourquoi supprimez-vous votre compte ?
        </Titre>

        <Form
          class="u-flex u-flex-column u-flex-align-center"
          @submit.prevent="supprimerCompte"
        >
          <List>
            <StateOverlay
              :is-loading="isLoading"
              overlay
            >
              <ListItem
                v-for="motif in motifs"
                :key="motif.id"
              >
                <FormRadio
                  v-model="model.motif_id"
                  name="radio"
                  class="u-mb-sm"
                  :value="motif.id"
                >
                  {{ motif.valeur }}
                </Formradio>
              </ListItem>
            </StateOverlay>
          </List>
          <FormError
            v-if="errorMotifs"
            :error="errorMotifs"
          />
          <Divider
            dotted
            class="u-full-width u-mt-md u-mb-md"
          />
          <Titre
            tag="h2"
            size="sm"
            class="u-text-center u-mb-md"
          >
            Pour des raisons de sécurité, veuillez saisir votre mot de passe
          </Titre>
          <FormGroup size="lg">
            <FormInput
              id="password"
              v-model="model.password"
              type="password"
            >
              Mot de passe*
            </FormInput>
          </FormGroup>
          <FormError
            v-if="error"
            :error="error"
          />
          <Button
            variant="alert"
            radius="full"
            size="md"
            type="submit"
            value="Supprimer mon compte Internet"
            class="u-mt-lg"
            :disabled="!model.motif_id || !model.password"
          />
        </Form>
      </template>

      <template v-else-if="etape === 'confirmation'">
        <Icon
          name="check-circle"
          variant="success"
          size="lg"
          class="u-mb-sm"
        />

        <Titre
          tag="h1"
          variant="primary"
          size="md"
          class="u-text-center u-mb-lg"
        >
          Votre compte a bien été supprimé
        </Titre>

        <p class="u-text-center">
          Nous espérons vous revoir bientôt.
        </p>

        <Button
          variant="primary"
          radius="full"
          size="md"
          class="u-mt-lg"
          @click="$emit('close')"
        >
          Ok j’ai compris
        </Button>
      </template>

      <template v-else-if="etape === 'error'">
        <Icon
          name="alert-circle"
          variant="primary"
          size="lg"
          class="u-mb-sm"
        />

        <Titre
          tag="h1"
          variant="primary"
          size="md"
          class="u-text-center u-mb-sm"
        >
          La suppression de compte n’est pas possible pour l’instant
        </Titre>
        <p class="u-text-center">
          Votre compte Cora ne peut pas être supprimé pour l’instant car vous avez des commandes ou un abonnement livraison en cours.
          <br><br>
          Vous pourrez supprimer votre compte une fois vos commandes et abonnements terminés
        </p>
        <Button
          variant="primary"
          radius="full"
          size="md"
          class="u-mt-lg"
          @click="$emit('close')"
        >
          Ok j’ai compris
        </Button>
      </template>

      <template v-else>
        <Icon
          name="alert-circle"
          variant="primary"
          size="lg"
          class="u-mb-sm"
        />
        <Titre
          tag="h1"
          variant="primary"
          size="md"
          class="u-text-center u-mb-sm"
        >
          Êtes-vous sûr de vouloir supprimer votre compte Internet ?
        </Titre>
        <p class="u-text-center">
          Avant de procéder à cette action définitive, il vous est possible de modifier votre adresse email ou votre mot de passe.
        </p>
        <Divider
          dotted
          size="sm"
          class="u-full-width u-my-lg"
        />
        <Titre
          tag="h2"
          size="sm"
          class="u-text-center u-mb-sm"
        >
          Que se passera-t-il si vous supprimez votre compte ?
        </Titre>

        <List disc>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Cette action est définitive et sera effective immédiatement</span>
          </ListItem>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Vous ne pourrez plus consulter l’historique de vos commandes passées sur cora.fr. Pour les commandes avec des garanties, vous pourrez obtenir vos tickets auprès du Village des Services. </span>
          </ListItem>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Vous perdrez l’accès à votre carte de fidélité dématérialisée. </span>
          </ListItem>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Vous ne pourrez plus consulter vos tickets de caisse magasin.</span>
          </ListItem>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Vos cartes bancaires enregistrées seront supprimées.</span>
          </ListItem>
          <ListItem class="u-color-primary u-mb-lg">
            <span class="u-color-text">Vous ne recevrez plus de communication newsletter de la part de Cora. Si vous souhaitez continuer de recevoir les communications liées à votre carte, veuillez vous rendre au Village des Services de votre magasin.</span>
          </ListItem>
          <ListItem class="u-color-primary">
            <span class="u-color-text">Vous continuerez de recevoir les communications par SMS de la part de Cora dans le cadre de votre adhésion au programme de fidélité. Si vous ne souhaitez plus les recevoir, répondez “STOP” aux sms reçus ou rendez-vous au Village des Services de votre magasin.</span>
          </ListItem>
        </List>
        <Divider
          dotted
          class="u-full-width u-mt-md u-mb-md"
        />
        <FormCheckbox
          v-model="isChecked"
          class="u-flex-self-start u-mb-sm u-text-left"
        >
          <p class="u-color-grey-1 u-font-14">
            En cochant cette case, j’ai bien compris les impacts de la suppression de mon compte
          </p>
        </FormCheckbox>
        <div class="u-text-center u-mt-lg u-mb-lg">
          <Button
            variant="primary"
            radius="full"
            size="md"
            :disabled="!isChecked"
            @click="$emit('step2')"
          >
            Continuer
          </Button>
        </div>
      </template>
    </div>
  </Container>
</template>

<style lang="scss" scoped>
</style>
